import Button from 'src/modules/shared/components/atoms/buttons/button';
import {
  PiPause,
  PiPlay,
  PiStop,
  PiPhoneX,
  PiMicrophoneSlash,
  PiMicrophone,
  PiSkipForward,
} from 'react-icons/pi';
import Dialog from 'src/modules/shared/components/molecules/dialog';
import { AnimatedDot } from 'src/modules/shared/components/atoms/indicators/animated-dot';
import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { formatPhoneNumber } from 'src/modules/shared/utils/formatting/phone.util';
import { classes } from 'src/modules/shared/utils/styles.util';
import { useSpeedDialControls } from '../../../hooks/speed-dialer/controls';
import { Text } from 'src/modules/shared/components/atoms/text';
import { Separator } from 'src/modules/shared/components/atoms/separator';

export const SpeedDialControls = () => {
  const {
    isPaused,
    isDialerActive,
    isCallActive,
    isMuted,
    currentQueueItem,
    currentPhoneNumber,
    currentAttempts,
    maxAttempts,
    computedRemainingCalls,
    showStopConfirmation,
    handleMute,
    handleEndCall,
    handlePlayPause,
    handleSkip,
    handleConfirmStop,
    setShowStopConfirmation,
  } = useSpeedDialControls();

  const contactLabel = currentQueueItem ? currentQueueItem.contactLabel : '';
  const formattedPhoneNumber = currentPhoneNumber
    ? formatPhoneNumber(currentPhoneNumber)
    : '';

  const title = contactLabel ? contactLabel : formattedPhoneNumber;
  const subtitle = contactLabel ? formattedPhoneNumber : '';
  const remainingCalls = `${computedRemainingCalls} restantes`;

  if (!isDialerActive && !isCallActive) return null;

  return (
    <>
      <div className="flex items-center gap-4 px-4 py-[5.5px] rounded-full bg-primary-50 justify-between min-w-0 max-w-full overflow-x-auto flex-nowrap">
        {isDialerActive && (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {/* TODO: check when implementing i18n */}
              <Tooltip
                content={`Intento ${currentAttempts + 1} de ${maxAttempts}`}
              >
                <div className="flex items-center gap-1.5 mr-2">
                  {[...Array(maxAttempts)].map((_, attempt) => (
                    <AnimatedDot
                      key={attempt}
                      className={classes(
                        attempt < currentAttempts &&
                          'animate-none !bg-call-500',
                        attempt > currentAttempts &&
                          'animate-none !bg-neutral-200',
                        attempt === currentAttempts &&
                          (!isPaused ? '!bg-call-500' : '!bg-neutral-500')
                      )}
                      size="sm"
                      isPulsing={attempt === currentAttempts}
                    />
                  ))}
                </div>
              </Tooltip>
              <div className="flex items-center gap-2 min-w-0">
                <Text
                  size="small"
                  weight="regular"
                  className="truncate max-w-[180px]"
                  title={title}
                >
                  {title}
                </Text>
                {subtitle && (
                  <Text
                    size="tiny"
                    weight="regular"
                    color="secondary"
                    className="whitespace-nowrap"
                  >
                    {subtitle}
                  </Text>
                )}
              </div>
            </div>
            <Text
              size="tiny"
              weight="regular"
              className="whitespace-nowrap leading-tight"
            >
              {remainingCalls}
            </Text>
          </div>
        )}

        <div className="flex items-center gap-1.5">
          <div className="flex items-center gap-2">
            {/* TODO: check when implementing i18n */}
            <Tooltip
              content={
                isMuted ? 'Activa el micrófono' : 'Silencia el micrófono'
              }
            >
              <Button
                variant="primary"
                appearance="ghost"
                size="xs"
                onClick={handleMute}
                isIconOnly
                disabled={!isCallActive}
              >
                {isMuted ? (
                  <PiMicrophoneSlash className="w-4 h-4" />
                ) : (
                  <PiMicrophone className="w-4 h-4" />
                )}
              </Button>
            </Tooltip>
            {/* TODO: check when implementing i18n */}
            <Tooltip content="Termina la llamada actual">
              <Button
                variant="danger"
                appearance="ghost"
                size="xs"
                onClick={handleEndCall}
                isIconOnly
                disabled={!isCallActive}
              >
                <PiPhoneX className="w-4 h-4" />
              </Button>
            </Tooltip>
          </div>

          {isDialerActive && (
            <>
              <Separator orientation="vertical" />

              <div className="flex items-center gap-2">
                {/* TODO: check when implementing i18n */}
                <Tooltip content="Saltar llamada actual">
                  <Button
                    variant="primary"
                    appearance="ghost"
                    size="xs"
                    onClick={handleSkip}
                    isIconOnly
                    disabled={!isPaused}
                  >
                    <PiSkipForward className="w-4 h-4" />
                  </Button>
                </Tooltip>
                {/* TODO: check when implementing i18n */}
                <Tooltip
                  content={
                    isPaused
                      ? 'Reanuda la sesión'
                      : 'Pausa la sesión y finaliza la llamada actual'
                  }
                >
                  <Button
                    variant="primary"
                    appearance="ghost"
                    size="xs"
                    onClick={handlePlayPause}
                    isIconOnly
                  >
                    {isPaused ? (
                      <PiPlay className="w-4 h-4" />
                    ) : (
                      <PiPause className="w-4 h-4" />
                    )}
                  </Button>
                </Tooltip>
                {/* TODO: check when implementing i18n */}
                <Tooltip content="Finaliza la sesión y la llamada actual">
                  <Button
                    variant="danger"
                    appearance="ghost"
                    size="xs"
                    onClick={() => setShowStopConfirmation(true)}
                    isIconOnly
                  >
                    <PiStop className="w-4 h-4" />
                  </Button>
                </Tooltip>
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog
        isOpen={showStopConfirmation}
        onClose={() => setShowStopConfirmation(false)}
        // TODO: check when implementing i18n
        title="Confirmar"
        description={
          isCallActive
            ? '¿Estás seguro que deseas finalizar la sesión? La llamada actual será terminada.'
            : '¿Estás seguro que deseas finalizar la sesión?'
        }
        confirmLabel="Finalizar"
        cancelLabel="Cancelar"
        onConfirm={handleConfirmStop}
        variant="danger"
      />
    </>
  );
};
